/**
 * 企微 JS-SDK 封装（聊天侧边栏用，顾问陪跑 PR⑦）。
 *
 * 顺序固定：载入 jweixin → wx.config（企业签名）→ wx.agentConfig（应用签名）→ 才能调
 * getCurExternalContact / sendChatMessage。签名按「当前页面地址（不含 #）」计算，地址变了要重签。
 *
 * 本地开发（vite dev）在普通浏览器里没有企微环境：侧边栏页带 ?mockExternalUserId= 时走假实现，
 * 生产构建里这条分支不存在。
 */
import { getWecomJssdkConfig } from '../api/wecom';

const JWEIXIN_SRC = 'https://res.wx.qq.com/open/js/jweixin-1.2.0.js';

interface WxLike {
  config(c: Record<string, unknown>): void;
  ready(fn: () => void): void;
  error(fn: (e: unknown) => void): void;
  agentConfig(c: Record<string, unknown>): void;
  invoke(api: string, args: Record<string, unknown>, cb: (res: { err_msg?: string; userId?: string }) => void): void;
}

declare global {
  interface Window {
    wx?: WxLike;
  }
}

export function isWecomBrowser(): boolean {
  return typeof navigator !== 'undefined' && /wxwork/i.test(navigator.userAgent);
}

function loadScript(src: string): Promise<void> {
  if (window.wx) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('企业微信 JS-SDK 加载失败'));
    document.head.appendChild(s);
  });
}

let ready: Promise<WxLike> | null = null;

/** 初始化一次（同一页面复用）；失败后允许重试 */
export function initWecomJssdk(): Promise<WxLike> {
  if (ready) return ready;
  ready = (async () => {
    await loadScript(JWEIXIN_SRC);
    const wx = window.wx!;
    const cfg = await getWecomJssdkConfig(window.location.href.split('#')[0]);
    await new Promise<void>((resolve, reject) => {
      wx.config({
        beta: true,
        debug: false,
        appId: cfg.corpId,
        timestamp: cfg.config.timestamp,
        nonceStr: cfg.config.nonceStr,
        signature: cfg.config.signature,
        jsApiList: ['agentConfig'],
      });
      wx.ready(() => resolve());
      wx.error((e) => reject(new Error(`wx.config 失败：${JSON.stringify(e)}`)));
    });
    await new Promise<void>((resolve, reject) => {
      wx.agentConfig({
        corpid: cfg.corpId,
        agentid: cfg.agentId,
        timestamp: cfg.agentConfig.timestamp,
        nonceStr: cfg.agentConfig.nonceStr,
        signature: cfg.agentConfig.signature,
        jsApiList: ['getContext', 'getCurExternalContact', 'sendChatMessage'],
        success: () => resolve(),
        fail: (e: unknown) => reject(new Error(`wx.agentConfig 失败：${JSON.stringify(e)}`)),
      });
    });
    return wx;
  })().catch((e) => {
    ready = null;
    throw e;
  });
  return ready;
}

/** 当前聊天的外部联系人 userId（只在单聊侧边栏里有） */
export async function getCurrentExternalUserId(): Promise<string> {
  const wx = await initWecomJssdk();
  return new Promise((resolve, reject) => {
    wx.invoke('getCurExternalContact', {}, (res) => {
      if (res.err_msg === 'getCurExternalContact:ok' && res.userId) resolve(res.userId);
      else reject(new Error('请在和客户的单聊里打开侧边栏'));
    });
  });
}

export interface MiniprogramCardMessage {
  appid: string;
  title: string;
  imgUrl: string;
  /** 小程序页面路径（不带 .html），如 pages/book-reader/index */
  page: string;
  query: string;
}

/**
 * 企微要求小程序消息的 page 以 .html 结尾，否则客户在微信里点开会提示找不到页面。
 */
export function toWecomPagePath(page: string, query: string): string {
  return query ? `${page}.html?${query}` : `${page}.html`;
}

/** 把小程序卡片插入当前聊天的输入框（enterChat:false 留在侧边栏；由销售在企微里点发送） */
export async function sendMiniprogramCard(msg: MiniprogramCardMessage): Promise<boolean> {
  const wx = await initWecomJssdk();
  return new Promise((resolve, reject) => {
    wx.invoke(
      'sendChatMessage',
      {
        msgtype: 'miniprogram',
        enterChat: false,
        miniprogram: { appid: msg.appid, title: msg.title, imgUrl: msg.imgUrl, page: toWecomPagePath(msg.page, msg.query) },
      },
      (res) => {
        if (res.err_msg === 'sendChatMessage:ok') resolve(true);
        else if (res.err_msg?.includes('cancel')) resolve(false);
        else reject(new Error(res.err_msg || '发送失败'));
      },
    );
  });
}
